/*
 * Porta-joias free-gift auto-add + cart re-sync bridge
 * ------------------------------------------------------------------
 * WHY THIS EXISTS (investigated live 2026-07):
 *   This store runs two SkaiLama free-gift apps at once — BOGOS
 *   (bogos-io-free-gift) and Kite (kite-discount-free-gift). Kite powers
 *   the "TODAS POR R$ 99" custom discount and MUST keep working; BOGOS
 *   owns the porta-joias free gift.
 *
 *   BOGOS's gift is auto-added by its LEGACY engine, which only activates
 *   when it detects a supported cart app (e.g. UpCart). On the live store
 *   (UpCart) that engine loads the offer (FGSECOMAPP.offers === 1) and the
 *   gift is added automatically. On this theme's NATIVE cart, BOGOS detects
 *   no supported cart app, its legacy engine stays inert (offers === 0),
 *   and the gift is never added. That is the whole bug: "works on UpCart,
 *   breaks on the native cart".
 *
 *   Instead of forcing two minified apps to coexist in one global
 *   namespace, this script reproduces ONLY the porta-joias rule in the
 *   theme. It never touches Kite's R$99 discount (a server-side Shopify
 *   Function), so both promotions work together.
 *
 * WHAT IT DOES:
 *   - Resolves the gift variant from its stable product handle, so it keeps
 *     working even if BOGOS regenerates the gift product (the variant id
 *     has already changed once).
 *   - After any cart mutation (and on load) it counts the non-gift item
 *     quantity: at/above the threshold it ensures exactly ONE gift line;
 *     below it, it removes the gift. The gift product is natively R$0.
 *   - Re-pulls the theme's custom drawer from the server (cart:refresh),
 *     because the drawer paints from a snapshot in its own AJAX response
 *     and never re-reads it otherwise.
 *
 * SAFETY: idempotent (only ever keeps qty 1 of the gift; if BOGOS ever
 *   starts adding the same variant, it is not duplicated); the reconcile is
 *   guarded so it cannot overlap or loop.
 */
(function () {
  if (window.__bogosCartSyncInstalled) return;
  window.__bogosCartSyncInstalled = true;

  /* ---- Offer config (mirrors the BOGOS "PORTA-JOIAS DE BRINDE" offer) ---- */
  // BOGOS regenerates the gift (clone) product every time the offer is
  // re-created, and the new clone gets a DIFFERENT handle (it 404s the old
  // one). So we don't trust a single handle: at runtime we (1) discover the
  // active gift handle from BOGOS's own offer data (window.fgData) and (2)
  // fall back to these known handles, newest first. Whatever resolves to a
  // real product on this store wins, so a future offer swap keeps working.
  var GIFT_HANDLE_CANDIDATES = [
    "porta-joias-murano-em-veludo-sca_clone_freegift", // active offer (2026-07)
    "porta-joias-sca_clone_freegift", // previous offer (now 404s)
  ];
  // Any clone-gift handle ends in this suffix; used to sniff the handle out of
  // window.fgData regardless of BOGOS's blob structure.
  var CLONE_SUFFIX = "sca_clone_freegift";
  // Fallback minimum qualifying (non-gift) quantity to earn the gift.
  // Overridden by the live BOGOS offer condition when available.
  var THRESHOLD_QTY = 6;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";

  var MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/i;
  var DEBOUNCE_MS = 550;

  var timer = null;
  var reconciling = false;
  var selfMutating = false; // true only while WE add/remove the gift
  var giftVariantId = null; // resolved lazily from a candidate handle
  var giftHandle = null; // the handle that actually resolved
  var giftResolved = false;

  // If BOGOS's data is on the page, prefer its live threshold.
  try {
    var offer = window.fgData && window.fgData.offers && window.fgData.offers[0];
    var cond = offer && offer.offer_conditions && offer.offer_conditions[0];
    if (cond && cond.unit === "number_of_products" && cond.min_amount) {
      var min = parseInt(cond.min_amount, 10);
      if (min > 0) THRESHOLD_QTY = min;
    }
  } catch (e) {}

  function pathnameOf(url) {
    try { return new URL(url, window.location.origin).pathname; }
    catch (e) { return String(url || "").split("?")[0].split("#")[0]; }
  }
  function isCartMutation(url) {
    if (!url) return false;
    return MUTATION_RE.test(pathnameOf(url)) || MUTATION_RE.test(String(url));
  }

  // Sniff any "...porta-joia...sca_clone_freegift" handles out of BOGOS's
  // offer blob, so a freshly-activated porta-joias offer is picked up
  // without editing this file. Scoped to "porta-joia" specifically (not any
  // "sca_clone_freegift" handle) — this store now also runs a separate
  // "colar de letra" gift offer (see assets/bogos-letter-necklace-gift.js),
  // whose own clone products share the same generic suffix; without this
  // scope, this discovery would find THAT offer's gift and silently
  // auto-add it here (no letter/style chosen), which then makes the letter
  // picker hide itself thinking a gift was already added — a real bug hit
  // live 2026-08-11.
  function discoverHandlesFromFgData() {
    var found = [];
    try {
      var blob = JSON.stringify(window.fgData || {});
      var re = /"handle"\s*:\s*"([^"]*porta-joia[^"]*sca_clone_freegift[^"]*)"/gi;
      var m;
      while ((m = re.exec(blob))) if (found.indexOf(m[1]) === -1) found.push(m[1]);
    } catch (e) {}
    return found;
  }

  function tryHandle(handle) {
    return fetch(ROOT + "products/" + handle + ".js", { headers: { Accept: "application/json" }, cache: "no-store", credentials: "same-origin" })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) { return p && p.variants && p.variants[0] ? p : null; })
      .catch(function () { return null; });
  }

  // Resolve the gift variant by trying discovered + known handles in order and
  // keeping the first that maps to a real product on this store.
  function resolveGiftVariant() {
    if (giftResolved) return Promise.resolve(giftVariantId);
    var candidates = discoverHandlesFromFgData().concat(GIFT_HANDLE_CANDIDATES);
    var seen = {};
    candidates = candidates.filter(function (h) { return h && !seen[h] && (seen[h] = 1); });

    var i = 0;
    function next() {
      if (i >= candidates.length) { giftResolved = true; return giftVariantId; }
      var handle = candidates[i++];
      return tryHandle(handle).then(function (p) {
        if (p) {
          giftHandle = p.handle || handle;
          giftVariantId = p.variants[0].id;
          giftResolved = true;
          return giftVariantId;
        }
        return next();
      });
    }
    return Promise.resolve(next());
  }

  function getCart() {
    return fetch(ROOT + "cart.js", { headers: { Accept: "application/json" }, cache: "no-store", credentials: "same-origin" })
      .then(function (r) { return r.json(); });
  }

  function addGift() {
    selfMutating = true;
    return fetch(ROOT + "cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ id: giftVariantId, quantity: 1 }),
    }).finally(function () { selfMutating = false; });
  }

  function setGiftQty(lineKey, qty) {
    selfMutating = true;
    return fetch(ROOT + "cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ id: lineKey, quantity: qty }),
    }).finally(function () { selfMutating = false; });
  }

  function isGiftItem(item) {
    if (giftHandle && item.handle === giftHandle) return true;
    if (giftVariantId && (item.variant_id === giftVariantId || item.id === giftVariantId)) return true;
    // Also treat any stale porta-joias clone-gift line as the gift (covers a
    // line left by a previous offer, so it gets removed below threshold
    // instead of counted). Scoped to "porta-joia" so this never claims the
    // separate "colar de letra" offer's gift line (see discoverHandlesFromFgData).
    return typeof item.handle === "string" && item.handle.indexOf("porta-joia") !== -1 && item.handle.indexOf(CLONE_SUFFIX) !== -1;
  }

  // Bring the cart in line with the offer rule. Resolves to true if changed.
  function reconcileGift() {
    if (reconciling) return Promise.resolve(false);
    reconciling = true;
    return resolveGiftVariant()
      .then(function (vid) {
        if (!vid) return false; // couldn't resolve the gift product → do nothing
        return getCart().then(function (cart) {
          var items = (cart && cart.items) || [];
          var giftLine = null;
          var qualifyingQty = 0;
          items.forEach(function (it) {
            if (isGiftItem(it)) giftLine = it;
            else qualifyingQty += it.quantity;
          });
          var qualifies = qualifyingQty >= THRESHOLD_QTY;

          if (qualifies && !giftLine) return addGift().then(function () { return true; });
          if (!qualifies && giftLine) return setGiftQty(giftLine.key, 0).then(function () { return true; });
          if (qualifies && giftLine && giftLine.quantity !== 1) return setGiftQty(giftLine.key, 1).then(function () { return true; });
          return false;
        });
      })
      .catch(function () { return false; })
      .finally(function () { reconciling = false; });
  }

  function runSync() {
    // Only dispatch when something was actually changed (added/removed) —
    // this script is now inert given the porta-joias offer is discontinued,
    // and an unconditional dispatch here was forcing the cart drawer to
    // tear down and rebuild ~550ms after every mutation for no reason,
    // racing with the separate colar-de-letra picker's own re-render and
    // causing it to flash and disappear (found live 2026-08-11).
    reconcileGift().then(function (changed) {
      if (changed) document.dispatchEvent(new CustomEvent("cart:refresh"));
    });
  }

  function schedule() {
    if (selfMutating) return; // ignore our own gift writes
    if (timer) clearTimeout(timer);
    timer = setTimeout(function () { timer = null; runSync(); }, DEBOUNCE_MS);
  }

  /* ---- Patch fetch ---- */
  if (typeof window.fetch === "function") {
    var originalFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === "string" ? input : (input && input.url);
      var mutation = isCartMutation(url);
      var result = originalFetch.apply(this, arguments);
      if (mutation && result && typeof result.then === "function") result.then(schedule, schedule);
      return result;
    };
  }

  /* ---- Patch XMLHttpRequest ---- */
  if (window.XMLHttpRequest && window.XMLHttpRequest.prototype) {
    var proto = window.XMLHttpRequest.prototype;
    var originalOpen = proto.open;
    proto.open = function (method, url) {
      this.__bogosIsCartMutation = isCartMutation(url);
      return originalOpen.apply(this, arguments);
    };
    var originalSend = proto.send;
    proto.send = function () {
      if (this.__bogosIsCartMutation) this.addEventListener("loadend", schedule);
      return originalSend.apply(this, arguments);
    };
  }

  /* ---- Reconcile once on load ---- */
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", runSync);
  } else {
    runSync();
  }
})();
